import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { fetchWithRetry } from '@/lib/apiRequest';
import type { PublicationInboxItem } from '@/hooks/usePublicationInbox';

export type PublicationInboxHistoryItem = {
  kind:
    | 'publication'
    | 'ai_job'
    | 'analysis'
    | 'review'
    | 'operationalization'
    | 'deadline'
    | 'task'
    | 'exception';
  title: string;
  detail: string | null;
  at: string | null;
};

export type PublicationInboxDetail = {
  schemaVersion: string;
  item: PublicationInboxItem;
  publication: {
    id: string;
    provider: string;
    source: string | null;
    externalId: string | null;
    sourceName: string | null;
    tribunal: string | null;
    processNumber: string | null;
    title: string;
    content: string;
    summary: string | null;
    availableAt: string | null;
    publishedAt: string | null;
    capturedAt: string;
    lawyerOab: string | null;
    lawyerName: string | null;
    partyNames: string | null;
    rawJson: unknown;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
  caseContext: PublicationInboxItem['context'];
  analysis: {
    summary?: {
      title?: string | null;
      concise?: string | null;
      context?: string | null;
    } | null;
    classification?: {
      communication?: string | null;
      subject?: string | null;
      event?: string | null;
      priority?: string | null;
    } | null;
    action?: {
      requiresAction?: string | null;
      actionType?: string | null;
      suggestedAction?: string | null;
      affectedParty?: string | null;
    } | null;
    deadline?: {
      source?: string | null;
      value?: number | null;
      unit?: string | null;
      countType?: string | null;
      legalBasis?: string | null;
      requiresConfirmation?: boolean | null;
    } | null;
    warnings?: unknown[];
    confidence?: {
      overall?: string | null;
      action?: string | null;
      deadline?: string | null;
    } | null;
  } | null;
  review: {
    id: string;
    analysisRunId: string;
    decision: string;
    reviewedAnalysis: unknown;
    notes: string | null;
    reviewedByName: string | null;
    reviewedAt: string | null;
  } | null;
  operationalization: {
    id: string;
    state: string;
    reviewId: string;
    procedureId: string | null;
    caseDeadlineId: string | null;
    taskId: string | null;
    lastErrorCode: string | null;
    lastErrorMessage: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  } | null;
  deadline: {
    id: string;
    status: string;
    dueDate: string | null;
    taskId: string | null;
    sourceType: string | null;
    sourceId: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  } | null;
  task: {
    id: string;
    status: string;
    title: string | null;
    dueDate: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  } | null;
  exceptions: Array<{
    id: string;
    severity: string | null;
    riskLevel: string | null;
    title: string;
    detail: string | null;
    entityType: string | null;
    entityId: string | null;
    createdAt: string | null;
  }>;
  history: PublicationInboxHistoryItem[];
};

type DetailResponse =
  PublicationInboxDetail & {
    success: boolean;
    error?: string;
  };

export function usePublicationInboxDetail(
  publicationId: string | null
) {
  const [detail, setDetail] =
    useState<PublicationInboxDetail | null>(
      null
    );
  const [isLoading, setIsLoading] =
    useState(false);
  const [error, setError] =
    useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!publicationId) {
      setDetail(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithRetry(
        `/api/publications/${encodeURIComponent(
          publicationId
        )}/inbox`,
        {},
        {
          timeoutMs: 15000,
          retries: 1,
          retryDelayMs: 300,
        }
      );

      const data =
        (await response.json()) as DetailResponse;

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
            'Não foi possível carregar o detalhe da publicação.'
        );
      }

      const {
        success: _success,
        error: _error,
        ...resolved
      } = data;

      void _success;
      void _error;

      setDetail(resolved);
    } catch (loadError) {
      setDetail(null);
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Não foi possível carregar o detalhe da publicação.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [publicationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    detail,
    isLoading,
    error,
    refresh,
  };
}